/**
 * Centralized validation utilities for forms
 * Provides Zod schemas, error handling, and reactive validation composables
 */

import { z } from 'zod'
import type { Ref } from 'vue'
import { watch } from 'vue'
import {
  setupValidationI18n,
  createValidationMessages,
  translateValidationMessage,
  onValidationI18nChange
} from './validation-i18n'

// Export i18n setup function and helpers
export { setupValidationI18n, translateValidationMessage, onValidationI18nChange }

/**
 * Configuration constants for validation
 */
export const ValidationConfig = {
  // Password requirements
  PASSWORD_MIN_LENGTH: 6,
  PASSWORD_STRONG_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 256,

  // Username requirements
  USERNAME_MIN_LENGTH: 4,

  // 2FA token requirements
  TFA_TOKEN_LENGTH: 6,

  // Confirmation code requirements
  CONFIRMATION_CODE_LENGTH: 6,

  // Amount validation - now dynamic from API

  // Bitcoin address length constraints
  BTC_ADDRESS_MIN_LENGTH: 25,
  BTC_ADDRESS_MAX_LENGTH: 62
} as const

/**
 * Regular expressions for validation
 */
export const ValidationRegex = {
  // Username pattern (letters, numbers, dots, underscores)
  USERNAME: /^[a-zA-Z0-9._]+$/,

  // 2FA token pattern (6 digits)
  TFA_TOKEN: /^\d{6}$/,

  // Confirmation code pattern (6 digits)
  CONFIRMATION_CODE: /^\d{6}$/,

  // Password strength patterns
  UPPERCASE: /[A-Z]/,
  LOWERCASE: /[a-z]/,
  NUMBERS: /[0-9]/,
  SPECIAL_CHARS: /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/,

  // Bitcoin address patterns
  BASE58_ALPHABET: /[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]/,

  // P2PKH addresses (start with 1)
  P2PKH_MAINNET: /^1[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,34}$/,

  // P2SH addresses (start with 3)
  P2SH_MAINNET: /^3[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,34}$/,

  // Bech32 addresses (start with bc1)
  BECH32_MAINNET: /^bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59}$/,

  // Bech32m addresses (start with bc1p)
  BECH32M_MAINNET: /^bc1p[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{37,58}$/,

  // Testnet addresses
  P2PKH_TESTNET: /^[mn][123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,34}$/,
  P2SH_TESTNET: /^2[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,34}$/,
  BECH32_TESTNET: /^tb1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59}$/,
  BECH32M_TESTNET: /^tb1p[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{37,58}$/,

  // Regtest addresses
  BECH32_REGTEST: /^bcrt1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59}$/,

  // Litecoin address patterns
  // P2PKH addresses (start with L)
  LTC_P2PKH_MAINNET: /^L[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{26,33}$/,

  // P2SH addresses (start with M or 3)
  LTC_P2SH_MAINNET: /^[M3][123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{26,33}$/,

  // Bech32 addresses (start with ltc1)
  LTC_BECH32_MAINNET: /^ltc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59}$/,

  // Bech32m addresses (start with ltc1p) - Taproot
  LTC_BECH32M_MAINNET: /^ltc1p[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{37,58}$/,

  // Testnet addresses  
  LTC_P2PKH_TESTNET: /^[mn][123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,34}$/,
  LTC_P2SH_TESTNET: /^2[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]{25,34}$/,
  LTC_BECH32_TESTNET: /^tltc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39,59}$/,

  // Bech32m testnet addresses (start with tltc1p) - Taproot
  LTC_BECH32M_TESTNET: /^tltc1p[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{37,58}$/
} as const

/**
 * Centralized validation error messages with i18n support
 * Ensures consistency across client and server validation
 */
export const ValidationMessages = createValidationMessages()

// Email validation schema
export const emailSchema = z
  .string()
  .min(1, { message: ValidationMessages.EMAIL_REQUIRED })
  .email({ message: ValidationMessages.EMAIL_INVALID })

// Username validation schema
export const usernameSchema = z
  .string()
  .min(1, { message: ValidationMessages.USERNAME_REQUIRED })
  .min(ValidationConfig.USERNAME_MIN_LENGTH, { message: ValidationMessages.USERNAME_MIN_LENGTH })
  .regex(ValidationRegex.USERNAME, {
    message: ValidationMessages.USERNAME_INVALID_CHARS
  })

// Password validation schema
export const passwordSchema = z
  .string()
  .min(1, { message: ValidationMessages.PASSWORD_REQUIRED })
  .min(ValidationConfig.PASSWORD_MIN_LENGTH, { message: ValidationMessages.PASSWORD_MIN_LENGTH })

// Login password schema aligned with backend minimum/maximum
export const loginPasswordSchema = z
  .string()
  .min(1, { message: ValidationMessages.PASSWORD_REQUIRED })
  .min(ValidationConfig.PASSWORD_STRONG_MIN_LENGTH, {
    message: ValidationMessages.PASSWORD_STRONG_MIN_LENGTH
  })
  .max(ValidationConfig.PASSWORD_MAX_LENGTH, {
    message: ValidationMessages.PASSWORD_LENGTH_RANGE
  })

// Strong password validation schema (for signup)
export const strongPasswordSchema = z
  .string()
  .min(1, { message: ValidationMessages.PASSWORD_REQUIRED })
  .min(ValidationConfig.PASSWORD_STRONG_MIN_LENGTH, {
    message: ValidationMessages.PASSWORD_STRONG_MIN_LENGTH
  })
  .regex(ValidationRegex.UPPERCASE, { message: ValidationMessages.PASSWORD_NEED_UPPERCASE })
  .regex(ValidationRegex.LOWERCASE, { message: ValidationMessages.PASSWORD_NEED_LOWERCASE })
  .regex(ValidationRegex.NUMBERS, { message: ValidationMessages.PASSWORD_NEED_NUMBER })
  .regex(ValidationRegex.SPECIAL_CHARS, { message: ValidationMessages.PASSWORD_NEED_SPECIAL })

/**
 * Creates a strong password validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 */
export function createStrongPasswordSchema() {
  return z
    .string()
    .min(1, { message: ValidationMessages.PASSWORD_REQUIRED })
    .min(ValidationConfig.PASSWORD_STRONG_MIN_LENGTH, {
      message: ValidationMessages.PASSWORD_STRONG_MIN_LENGTH
    })
    .regex(ValidationRegex.UPPERCASE, { message: ValidationMessages.PASSWORD_NEED_UPPERCASE })
    .regex(ValidationRegex.LOWERCASE, { message: ValidationMessages.PASSWORD_NEED_LOWERCASE })
    .regex(ValidationRegex.NUMBERS, { message: ValidationMessages.PASSWORD_NEED_NUMBER })
    .regex(ValidationRegex.SPECIAL_CHARS, { message: ValidationMessages.PASSWORD_NEED_SPECIAL })
}

// BTC address validation schema for guest login
export const btcAddressSchema = z
  .string()
  .min(1, { message: ValidationMessages.ADDRESS_REQUIRED })
  .refine(
    (address) => {
      // Extract address part (before the dot if workername is present)
      const addressPart = address.split('.')[0]
      return validateBitcoinAddress(addressPart)
    },
    {
      message: ValidationMessages.WALLET_BTC_INVALID
    }
  )

// Guest login schema (BTC address + any password)
export const guestLoginSchema = z.object({
  btcAddress: btcAddressSchema,
  password: z.string().min(1, { message: ValidationMessages.PASSWORD_GUEST_REQUIRED })
})

// Login form schema
export const loginSchema = z.object({
  username: usernameSchema,
  password: loginPasswordSchema
})

// 2FA token validation schema
export const twoFATokenSchema = z
  .string()
  .min(ValidationConfig.TFA_TOKEN_LENGTH, { message: ValidationMessages.TFA_FORMAT })
  .max(ValidationConfig.TFA_TOKEN_LENGTH, { message: ValidationMessages.TFA_FORMAT })
  .regex(ValidationRegex.TFA_TOKEN, { message: ValidationMessages.TFA_DIGITS_ONLY })


export function createTwoFATokenSchema() {
  return z
    .string()
    .min(ValidationConfig.TFA_TOKEN_LENGTH, { message: ValidationMessages.TFA_FORMAT })
    .max(ValidationConfig.TFA_TOKEN_LENGTH, { message: ValidationMessages.TFA_FORMAT })
    .regex(ValidationRegex.TFA_TOKEN, { message: ValidationMessages.TFA_DIGITS_ONLY })
}

// Confirmation code validation schema
export const confirmationCodeSchema = z
  .string()
  .min(1, { message: ValidationMessages.CONFIRMATION_CODE_REQUIRED })
  .transform((val) => val.trim()) // Trim whitespace
  .pipe(
    z.string()
      .min(1, { message: ValidationMessages.CONFIRMATION_CODE_REQUIRED })
      .length(ValidationConfig.CONFIRMATION_CODE_LENGTH, {
        message: ValidationMessages.CONFIRMATION_CODE_FORMAT
      })
      .regex(ValidationRegex.CONFIRMATION_CODE, {
        message: ValidationMessages.CONFIRMATION_CODE_DIGITS_ONLY
      })
  )

// Signup form schema
export const signupSchema = z
  .object({
    email: emailSchema,
    username: usernameSchema,
    password: strongPasswordSchema,
    confirmPassword: z.string().min(1, { message: ValidationMessages.PASSWORD_CONFIRM_REQUIRED }),
    confirmationCode: confirmationCodeSchema
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: ValidationMessages.PASSWORD_MISMATCH,
    path: ['confirmPassword']
  })

/**
 * Creates a signup validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 */
export function createSignupSchema() {
  const strongPassword = createStrongPasswordSchema()
  
  return z
    .object({
      email: emailSchema,
      username: usernameSchema,
      password: strongPassword,
      confirmPassword: z.string().min(1, { message: ValidationMessages.PASSWORD_CONFIRM_REQUIRED }),
      confirmationCode: confirmationCodeSchema
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: ValidationMessages.PASSWORD_MISMATCH,
      path: ['confirmPassword']
    })
}

// Reset password request schema
export const resetPasswordRequestSchema = z.object({
  email: emailSchema
})

// Reset password schema (with token)
export const resetPasswordSchema = z
  .object({
    password: strongPasswordSchema,
    confirmPassword: z.string().min(1, { message: ValidationMessages.PASSWORD_CONFIRM_REQUIRED }),
    token: z.string().min(1, { message: ValidationMessages.TOKEN_REQUIRED }),
    user_id: z.union([z.string(), z.number()]).transform((val) => String(val))
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: ValidationMessages.PASSWORD_MISMATCH,
    path: ['confirmPassword']
  })

/**
 * Creates a reset password validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 */
export function createResetPasswordSchema() {
  const strongPassword = createStrongPasswordSchema()
  
  return z
    .object({
      password: strongPassword,
      confirmPassword: z.string().min(1, { message: ValidationMessages.PASSWORD_CONFIRM_REQUIRED }),
      token: z.string().min(1, { message: ValidationMessages.TOKEN_REQUIRED }),
      user_id: z.union([z.string(), z.number()]).transform((val) => String(val))
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: ValidationMessages.PASSWORD_MISMATCH,
      path: ['confirmPassword']
    })
}

// Change password schema (for authenticated users)
export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, { message: ValidationMessages.PASSWORD_CURRENT_REQUIRED }),
    newPassword: strongPasswordSchema,
    confirmPassword: z
      .string()
      .min(1, { message: ValidationMessages.PASSWORD_CONFIRM_NEW_REQUIRED })
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: ValidationMessages.PASSWORD_MISMATCH,
    path: ['confirmPassword']
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: ValidationMessages.PASSWORD_SAME_AS_CURRENT,
    path: ['newPassword']
  })

/**
 * Creates a change password validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 */
export function createChangePasswordSchema() {
  const strongPassword = createStrongPasswordSchema()
  
  return z
    .object({
      currentPassword: z.string().min(1, { message: ValidationMessages.PASSWORD_CURRENT_REQUIRED }),
      newPassword: strongPassword,
      confirmPassword: z
        .string()
        .min(1, { message: ValidationMessages.PASSWORD_CONFIRM_NEW_REQUIRED })
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: ValidationMessages.PASSWORD_MISMATCH,
      path: ['confirmPassword']
    })
    .refine((data) => data.currentPassword !== data.newPassword, {
      message: ValidationMessages.PASSWORD_SAME_AS_CURRENT,
      path: ['newPassword']
    })
}

// Change email schema (for authenticated users)
export const changeEmailSchema = z.object({
  email: emailSchema,
  confirmationCode: confirmationCodeSchema
})

// Wallet address validation schema (for payment settings)
export const walletAddressSchema = z
  .string()
  .min(1, { message: ValidationMessages.WALLET_REQUIRED })
  .refine((address) => validateBitcoinAddress(address), {
    message: ValidationMessages.WALLET_INVALID
  })

/**
 * Creates a wallet address validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 * @param currency - The currency type ('BTC' or 'LTC')
 * @param networks - Array of allowed networks ('mainnet', 'testnet', 'regtest')
 */
export function createWalletAddressSchema(currency: 'BTC' | 'LTC' = 'BTC', networks?: string[]) {
  const messages = getCurrencyValidationMessages(currency)
  
  return z
    .string()
    .min(1, { message: messages.WALLET_REQUIRED })
    .refine((address) => validateCryptoAddress(address, currency, networks), {
      message: messages.WALLET_INVALID
    })
}


/**
 * Creates a dynamic wallet settings schema based on currency
 * @param currency - The currency type ('BTC' or 'LTC')
 */
export function createWalletSettingsSchema(currency: 'BTC' | 'LTC' = 'BTC') {
  return z.object({
    walletAddress: createWalletAddressSchema(currency),
    minimumAmount: createMinimumAmountSchema(),
    twoFactorCode: twoFATokenSchema
  })
}

// Minimum amount validation schema
export const minimumAmountSchema = z
  .string()
  .min(1, { message: ValidationMessages.AMOUNT_REQUIRED })
  .refine(
    (amount) => {
      const numAmount = parseFloat(amount)
      return !isNaN(numAmount) && numAmount > 0
    },
    {
      message: ValidationMessages.AMOUNT_POSITIVE
    }
  )
  .refine(
    (amount) => {
      const numAmount = parseFloat(amount)
      return numAmount >= 0.001 // Default minimum
    },
    {
      message: ValidationMessages.AMOUNT_RANGE
    }
  )
  .refine(
    (amount) => {
      const numAmount = parseFloat(amount)
      return numAmount <= 0.5 // Default maximum
    },
    {
      message: ValidationMessages.AMOUNT_RANGE
    }
  )

/**
 * Creates a minimum amount validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 * @param minAmount - Minimum allowed amount (default: 0.001)
 * @param maxAmount - Maximum allowed amount (default: 0.5)
 */
export function createMinimumAmountSchema(minAmount?: number, maxAmount?: number) {
  const min = minAmount ?? 0.001
  const max = maxAmount ?? 0.5
  
  return z
    .string()
    .min(1, { message: ValidationMessages.AMOUNT_REQUIRED })
    .refine(
      (amount) => {
        const numAmount = parseFloat(amount)
        return !isNaN(numAmount) && numAmount > 0
      },
      {
        message: ValidationMessages.AMOUNT_POSITIVE
      }
    )
    .refine(
      (amount) => {
        const numAmount = parseFloat(amount)
        return numAmount >= min
      },
      {
        message: ValidationMessages.AMOUNT_RANGE
      }
    )
    .refine(
      (amount) => {
        const numAmount = parseFloat(amount)
        return numAmount <= max
      },
      {
        message: ValidationMessages.AMOUNT_RANGE
      }
    )
}

// Wallet settings schema
export const walletSettingsSchema = z.object({
  walletAddress: walletAddressSchema,
  minimumAmount: minimumAmountSchema,
  twoFactorCode: twoFATokenSchema
})

/**
 * Creates a wallet settings validation schema with current i18n messages
 * Use this function instead of the static export for reactive translations
 */
export function createWalletSettingsSchemaBase() {
  return z.object({
    walletAddress: createWalletAddressSchema('BTC'),
    minimumAmount: createMinimumAmountSchema(),
    twoFactorCode: twoFATokenSchema
  })
}

/**
 * Validates Bitcoin address format
 * Supports P2PKH, P2SH, Bech32, and Bech32m formats
 * @param address - The address to validate
 * @param networks - Array of allowed networks ('mainnet', 'testnet', 'regtest')
 */
export function validateBitcoinAddress(address: string, networks?: string[]): boolean {
  if (!address || typeof address !== 'string') {
    return false
  }

  address = address.trim()

  // Default to mainnet if no networks specified (backwards compatibility)
  const allowedNetworks = networks?.length ? networks : ['mainnet']

  // Mainnet addresses
  if (allowedNetworks.includes('mainnet')) {
    if (ValidationRegex.P2PKH_MAINNET.test(address)) return true
    if (ValidationRegex.P2SH_MAINNET.test(address)) return true
    if (ValidationRegex.BECH32_MAINNET.test(address)) return true
    if (ValidationRegex.BECH32M_MAINNET.test(address)) return true
  }

  // Testnet addresses
  if (allowedNetworks.includes('testnet')) {
    if (ValidationRegex.P2PKH_TESTNET.test(address)) return true
    if (ValidationRegex.P2SH_TESTNET.test(address)) return true
    if (ValidationRegex.BECH32_TESTNET.test(address)) return true
    if (ValidationRegex.BECH32M_TESTNET.test(address)) return true
  }

  // Regtest addresses
  if (allowedNetworks.includes('regtest')) {
    if (ValidationRegex.BECH32_REGTEST.test(address)) return true
  }

  return false
}

/**
 * Validates Litecoin address format
 * Supports P2PKH (L...), P2SH (M.../3...), and Bech32 (ltc1...) formats
 * @param address - The address to validate
 * @param networks - Array of allowed networks ('mainnet', 'testnet')
 */
export function validateLitecoinAddress(address: string, networks?: string[]): boolean {
  if (!address || typeof address !== 'string') {
    return false
  }

  address = address.trim()

  // Default to mainnet if no networks specified (backwards compatibility)
  const allowedNetworks = networks?.length ? networks : ['mainnet']

  // Mainnet addresses
  if (allowedNetworks.includes('mainnet')) {
    if (ValidationRegex.LTC_P2PKH_MAINNET.test(address)) return true
    if (ValidationRegex.LTC_P2SH_MAINNET.test(address)) return true
    if (ValidationRegex.LTC_BECH32_MAINNET.test(address)) return true
    if (ValidationRegex.LTC_BECH32M_MAINNET.test(address)) return true
  }

  // Testnet addresses
  if (allowedNetworks.includes('testnet')) {
    if (ValidationRegex.LTC_P2PKH_TESTNET.test(address)) return true
    if (ValidationRegex.LTC_P2SH_TESTNET.test(address)) return true
    if (ValidationRegex.LTC_BECH32_TESTNET.test(address)) return true
    if (ValidationRegex.LTC_BECH32M_TESTNET.test(address)) return true
  }

  return false
}

/**
 * Validates cryptocurrency address format based on currency type
 * @param address - The address to validate
 * @param currency - The currency type ('BTC' or 'LTC')
 * @param networks - Array of allowed networks ('mainnet', 'testnet', 'regtest')
 */
export function validateCryptoAddress(address: string, currency: 'BTC' | 'LTC' = 'BTC', networks?: string[]): boolean {
  switch (currency.toUpperCase()) {
    case 'BTC':
      return validateBitcoinAddress(address, networks)
    case 'LTC':
      return validateLitecoinAddress(address, networks)
    default:
      console.warn(`Unsupported currency: ${currency}. Falling back to Bitcoin validation.`)
      return validateBitcoinAddress(address, networks)
  }
}

/**
 * Gets currency-specific error messages
 * @param currency - The currency type ('BTC' or 'LTC')
 */
export function getCurrencyValidationMessages(currency: 'BTC' | 'LTC' = 'BTC') {
  switch (currency.toUpperCase()) {
    case 'BTC':
      return {
        WALLET_REQUIRED: ValidationMessages.WALLET_BTC_REQUIRED,
        WALLET_INVALID: ValidationMessages.WALLET_BTC_INVALID
      }
    case 'LTC':
      return {
        WALLET_REQUIRED: ValidationMessages.WALLET_LTC_REQUIRED,
        WALLET_INVALID: ValidationMessages.WALLET_LTC_INVALID
      }
    default:
      return {
        WALLET_REQUIRED: ValidationMessages.WALLET_BTC_REQUIRED,
        WALLET_INVALID: ValidationMessages.WALLET_BTC_INVALID
      }
  }
}

/**
 * Extracts BTC address and worker name from guest login input
 */
export function parseBtcAddressInput(input: string): { address: string; workerName?: string } {
  const parts = input.split('.')
  return {
    address: parts[0],
    workerName: parts.length > 1 ? parts.slice(1).join('.') : undefined
  }
}

// Helper function to handle Zod validation errors
export function handleZodError(
  error: z.ZodError,
  formErrors: Record<string, string>
): Record<string, string> {
  const newErrors = { ...formErrors }

  error.errors.forEach((err) => {
    const field = err.path[0] as string
    if (field && field in newErrors) {
      newErrors[field] = err.message
    }
  })

  return newErrors
}

/**
 * Creates reactive field validator
 */
export function createFieldValidator(
  schema: z.ZodSchema,
  fieldValue: Ref<string>,
  formErrors: Record<string, string>,
  fieldName: string,
  additionalValidation?: (value: string) => string | null
) {
  return watch(fieldValue, (newValue) => {
    if (formErrors[fieldName] && newValue && newValue !== undefined) {
      try {
        schema.parse(newValue)
        if (additionalValidation) {
          const additionalError = additionalValidation(newValue)
          formErrors[fieldName] = additionalError || ''
        } else {
          formErrors[fieldName] = ''
        }
      } catch (error) {
        if (error instanceof z.ZodError) {
          formErrors[fieldName] = error.errors[0]?.message || `Invalid ${fieldName}`
        }
      }
    }
  })
}

/**
 * Password confirmation validator utility
 */
export function createPasswordConfirmValidator(
  confirmPasswordValue: Ref<string>,
  passwordValue: Ref<string>,
  formErrors: Record<string, string>,
  fieldName: string = 'confirmPassword'
) {
  return watch(confirmPasswordValue, (newValue) => {
    if (formErrors[fieldName] && newValue) {
      if (newValue === passwordValue.value) {
        formErrors[fieldName] = ''
      } else {
        formErrors[fieldName] = ValidationMessages.PASSWORD_MISMATCH
      }
    }
  })
}

/**
 * Create multiple field validators at once
 */
export interface FieldValidatorConfig {
  schema: z.ZodSchema
  fieldRef: Ref<string>
  errorKey: string
  additionalValidation?: (value: string) => string | null
}

export function createFormValidators(
  configs: FieldValidatorConfig[],
  formErrors: Record<string, string>
) {
  const watchers = configs.map(config => 
    createFieldValidator(
      config.schema,
      config.fieldRef,
      formErrors,
      config.errorKey,
      config.additionalValidation
    )
  )

  const stopWatchers = () => {
    watchers.forEach(stop => stop())
  }

  return { stopWatchers }
}


